import React from "react"
import styled, { css } from "styled-components"
import { up } from "styled-breakpoints"
import Link from "next/link"
import colors from "@constants/colors"
import { body, bodyBold, h5 } from "@constants/typography"
import { StyledLink } from "@components/buttons/LinkButton"

interface SplitSide {
  imageSrc?: string
  bgColor?: string
  text?: string
  textColor?: string
  upperSubtitle?: string
  subtitleColor?: string
  buttonText?: string
  buttonLink?: string
  buttonColor?: string
  buttonTextColor?: string
}

interface HeroImageSplitProps {
  imageLayout?: string
  leftSide: SplitSide
  rightSide: SplitSide
}

const getLayoutColumns = (layout?: string) => {
  if (!layout) return "2fr 1fr"
  const value = layout.trim().toLowerCase()

  if (value === "threefourth") return "3fr 1fr"
  if (value === "half") return "1fr 1fr"
  if (value === "onefourth") return "1fr 3fr"

  if (value.includes("half-half") || value.includes("half half")) {
    return "1fr 1fr"
  }

  const ratio = value.match(/(\d{1,2})\s*[/:-]\s*(\d{1,2})/)
  if (ratio) {
    const left = Number(ratio[1])
    const right = Number(ratio[2])
    if (left > 0 && right > 0) {
      return `${left}fr ${right}fr`
    }
  }

  if (value.includes("left") && value.includes("large")) return "2fr 1fr"
  if (value.includes("right") && value.includes("large")) return "1fr 2fr"
  if (value.includes("equal") || value.includes("50")) return "1fr 1fr"

  return "2fr 1fr"
}

const Section = styled.section`
  width: 100%;
  padding: 10px 0;
`

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0;
`

const SplitGrid = styled.div<{ columns: string }>`
  ${({ columns }) => css`
    display: grid;
    grid-template-columns: 1fr;
    gap: 2px;

    ${up("tablet")} {
      grid-template-columns: ${columns};
    }
  `}
`

const Panel = styled.div<{ imageSrc?: string; bgColor?: string }>`
  ${({ imageSrc, bgColor }) => css`
    position: relative;
    min-height: 420px;
    background-color: ${bgColor ? `#${bgColor}` : colors.slateDark};
    background-image: ${imageSrc
      ? `linear-gradient(rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.45)), url(${imageSrc})`
      : "none"};
    background-size: cover;
    background-position: center;
    overflow: hidden;

    ${up("tablet")} {
      min-height: 560px;
    }

    ${up("laptop")} {
      min-height: 660px;
    }
  `}
`

const Content = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 20px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  ${up("laptop")} {
    padding: 26px 24px;
    gap: 10px;
  }
`

const UpperSubtitle = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${body}
    margin: 0;
    color: ${color ? `#${color}` : colors.snow};
    opacity: 0.95;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 11px;
    line-height: 13px;
    font-weight: 600;
  `}
`

const DashMarker = styled.span<{ color?: string }>`
  ${({ color }) => css`
    display: inline-block;
    width: 26px;
    height: 5px;
    margin-bottom: 2px;
    border-radius: 2px;
    background: ${color ? `#${color}` : colors.snow};
  `}
`

const MainText = styled.p<{ color?: string }>`
  ${({ color }) => css`
    ${h5}
    margin: 0;
    color: ${color ? `#${color}` : colors.snow};
    text-transform: uppercase;
    white-space: pre-line;
    letter-spacing: 0.9px;
    font-size: 16px;
    line-height: 34px;

    ${up("laptop")} {
      font-size: 21px;
      line-height: 40px;
    }
  `}
`

const PanelButton = styled(StyledLink)`
  ${css`
    width: fit-content;
    align-self: flex-start;
    margin-top: 2px;
    min-height: 24px;
    height: 24px;
    padding: 0 12px;
    border-radius: 999px;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-size: 10px;
    line-height: 1;
    font-weight: 600;
    border: 1px solid rgba(0, 0, 0, 0.16);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.18);
    transition: opacity 160ms ease;

    &:hover,
    &:focus-visible {
      opacity: 0.7;
    }
  `}
`

const SideContent: React.FC<{ side: SplitSide }> = ({ side }) => {
  return (
    <Content>
      {(side.upperSubtitle || side.text) && (
        <DashMarker color={side.subtitleColor || side.textColor} />
      )}
      {side.upperSubtitle && (
        <UpperSubtitle color={side.subtitleColor}>
          {side.upperSubtitle}
        </UpperSubtitle>
      )}
      {side.text && <MainText color={side.textColor}>{side.text}</MainText>}
      {side.buttonText && side.buttonLink && (
        <Link href={side.buttonLink} passHref>
          <PanelButton
            $buttonColor={side.buttonColor}
            $buttonTextColor={side.buttonTextColor}
          >
            {side.buttonText}
          </PanelButton>
        </Link>
      )}
    </Content>
  )
}

const HeroImageSplit: React.FC<HeroImageSplitProps> = ({
  imageLayout,
  leftSide,
  rightSide,
}) => {
  return (
    <Section>
      <Container>
        <SplitGrid columns={getLayoutColumns(imageLayout)}>
          <Panel imageSrc={leftSide.imageSrc} bgColor={leftSide.bgColor}>
            <SideContent side={leftSide} />
          </Panel>
          <Panel imageSrc={rightSide.imageSrc} bgColor={rightSide.bgColor}>
            <SideContent side={rightSide} />
          </Panel>
        </SplitGrid>
      </Container>
    </Section>
  )
}

export default HeroImageSplit
